import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const FeaturedArticle = ({ article, onReadMore }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative w-full h-[550px] md:h-[650px] lg:h-[700px] rounded-3xl overflow-hidden group cursor-pointer shadow-2xl shadow-teal-500/20 border border-teal-500/30"
      onClick={() => onReadMore(article)}
    >
      {/* Image with Overlay */}
      <div className="absolute inset-0">
        <Image
          src={article?.image}
          alt={article?.imageAlt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/30 to-cyan-900/30 mix-blend-overlay" />
      </div>
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 lg:p-12">
        {/* Category Badge & Meta */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="px-5 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-bold rounded-full shadow-lg shadow-teal-500/50">
            {article?.category}
          </span>
          <div className="flex items-center gap-2 text-teal-300 text-sm font-medium bg-slate-900/50 backdrop-blur-sm px-4 py-2 rounded-full border border-teal-500/30">
            <Icon name="Clock" size={16} color="currentColor" />
            <span>{article?.readTime} min read</span>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 text-white line-clamp-2 group-hover:text-teal-300 transition-colors leading-tight"
        >
          {article?.title}
        </motion.h2>

        {/* Excerpt */}
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-base md:text-lg lg:text-xl text-slate-300 mb-8 line-clamp-2 leading-relaxed"
        >
          {article?.excerpt}
        </motion.p>

        {/* Author & Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex items-center justify-between bg-slate-900/60 backdrop-blur-xl rounded-2xl p-4 border border-teal-500/30"
        >
          <div className="flex items-center gap-4">
            <Image
              src={article?.author?.avatar}
              alt={article?.author?.avatarAlt}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-teal-500 shadow-lg shadow-teal-500/50"
            />
            <div>
              <div className="text-base md:text-lg font-bold text-white">
                {article?.author?.name}
              </div>
              <div className="text-sm text-teal-300">
                {article?.publishDate}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 text-slate-300">
            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg">
              <Icon name="Eye" size={20} color="#14b8a6" />
              <span className="text-sm font-semibold">{article?.views}</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg">
              <Icon name="MessageCircle" size={20} color="#14b8a6" />
              <span className="text-sm font-semibold">{article?.comments}</span>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-teal-500/0 to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
    </motion.div>
  );
};

export default FeaturedArticle;